import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  FormSection,
  FormRow,
  FormColumn,
  ButtonWrapper,
  UpgradeSummary,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  TotalRow,
} from './styles';
import {
  BackArrow,
  TopProgress,
  Typography,
  Select,
  Button,
  Input,
} from "../../../../components";
import { billingService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";

interface UpgradeOptions {
  cycle: {
    id: number;
    display_name: string;
    currency: string;
    period_in_days: number;
  };
  product: {
    id: number;
    name: string;
  };
  cycle_upgrades: Array<{
    id: number;
    display_name: string;
    currency: string;
    period_in_days: number;
    fixed_price: number;
  }>;
  upgrades: Array<{
    id: number;
    name: string;
    cycles: Array<{
      id: number;
      display_name: string;
      currency: string;
      fixed_price: number;
    }>;
  }>;
  configurable_options_upgrades: any[];
  existing_upgrade_invoice?: number;
  has_not_reached_service_cycles?: boolean;
  enduser_allow_service_cycle_change?: boolean;
  enduser_shorten_current_product_service_cycle?: boolean;
  enduser_shorten_new_product_service_cycle?: boolean;
}

interface UpgradeResult {
  display_name: string;
  product_upgrade_price: number;
  currency: string;
  taxes_applied: Record<string, number>;
  total_due: number;
}

interface Service {
  id: number;
  product: {
    name: string;
  };
  status: string;
}

const ServiceUpgradeForm: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const { serviceId } = useParams();
  usePageTitle(t("UPGRADE_SERVICE"));

  const [service, setService] = useState<Service | null>(null);
  const [upgradeOptions, setUpgradeOptions] = useState<UpgradeOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Form state
  const [selectedProduct, setSelectedProduct] = useState<number>(0);
  const [selectedCycle, setSelectedCycle] = useState<number>(0);
  const [confirmChanges, setConfirmChanges] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<UpgradeResult | null>(null);
  const [optionsChanged, setOptionsChanged] = useState(false);
  const [configOptionValues, setConfigOptionValues] = useState<{ [key: number]: string }>({});

  const getService = async () => {
    if (!serviceId) return;
    
    try {
      const res = await billingService.getService(serviceId);
      setService(res.data);
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || 'Error loading service'
      });
      navigate('/billing/services');
    }
  };

  const getUpgradeOptions = async (cycleId?: number, productId?: number) => {
    if (!serviceId) return;

    try {
      const res = await billingService.getServiceUpgradeOptions(serviceId, cycleId, productId);
      const options = res.data;
      setUpgradeOptions(options);

      // Set initial values on first load
      if (!cycleId && !productId) {
        setSelectedProduct(options.product.id);
        setSelectedCycle(options.cycle.id);

        // Initialize config option values
        const initialValues: { [key: number]: string } = {};
        if (options.configurable_options) {
          for (const option of options.configurable_options) {
            initialValues[option.option] = option.option_value;
          }
        }
        setConfigOptionValues(initialValues);

        setPageLoading(false);
      } else {
        // Update config options after cycle/product change
        const newValues: { [key: number]: string } = {};
        if (options.configurable_options_upgrades) {
          for (const option of options.configurable_options_upgrades) {
            // Try to keep similar values from current options
            const currentOption = upgradeOptions?.configurable_options?.find(
              (o: any) => o.option === option.id
            );
            if (currentOption) {
              newValues[option.id] = currentOption.option_value;
            } else {
              newValues[option.id] = '';
            }
          }
        }
        setConfigOptionValues(newValues);
      }
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || 'Error loading upgrade options'
      });
      if (!cycleId && !productId) {
        navigate(`/billing/services/${serviceId}`);
      }
    }
  };

  const checkUpgrade = async () => {
    if (!serviceId || !upgradeOptions) return;

    setLoading(true);
    try {
      const configOptions = [];
      for (const option of upgradeOptions.configurable_options_upgrades || []) {
        if (configOptionValues[option.id]) {
          configOptions.push({
            option: option.id,
            option_value: configOptionValues[option.id]
          });
        }
      }

      const upgradeData = {
        cycle: selectedCycle,
        product: selectedProduct,
        configurable_options: configOptions,
      };

      const res = await billingService.upgradeService(serviceId, upgradeData);
      setUpgradeResult(res.data);
      setConfirmChanges(true);
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_CHECKING_UPGRADE')
      });
    } finally {
      setLoading(false);
    }
  };

  const placeOrder = async () => {
    if (!serviceId || !upgradeResult) return;

    setLoading(true);
    try {
      const configOptions = [];
      for (const option of upgradeOptions?.configurable_options_upgrades || []) {
        if (configOptionValues[option.id]) {
          configOptions.push({
            option: option.id,
            option_value: configOptionValues[option.id]
          });
        }
      }

      const upgradeData = {
        cycle: selectedCycle,
        product: selectedProduct,
        configurable_options: configOptions,
        confirm: true,
      };

      const res = await billingService.upgradeService(serviceId, upgradeData);

      viewAlert({
        severity: 'success',
        message: t('UPGRADE_ORDER_PLACED_SUCCESSFULLY')
      });

      navigate(`/billing/invoices/${res.data.invoice}`);
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_PLACING_UPGRADE_ORDER')
      });
    } finally {
      setLoading(false);
    }
  };

  const checkIfOptionsChanged = () => {
    if (!upgradeOptions) return;

    const cycleChanged = selectedCycle !== upgradeOptions.cycle.id;
    const productChanged = selectedProduct !== upgradeOptions.product.id;

    // Check if any config options changed
    const configOptionsChanged = upgradeOptions.configurable_options_upgrades?.some(
      (option: any) => {
        const currentValue = configOptionValues[option.id];
        const originalOption = upgradeOptions.configurable_options?.find((o: any) => o.option === option.id);
        return currentValue !== originalOption?.option_value;
      }
    );

    setOptionsChanged(cycleChanged || productChanged || !!configOptionsChanged);
  };

  const handleCycleChange = async (cycleId: number) => {
    setSelectedCycle(cycleId);
    await getUpgradeOptions(cycleId, selectedProduct);
    checkIfOptionsChanged();
  };

  const handleProductChange = async (productId: number) => {
    setSelectedProduct(productId);
    const product = upgradeOptions?.upgrades.find(p => p.id === productId);
    if (product && product.cycles.length > 0) {
      // Find similar cycle or use first
      const similarCycle = product.cycles.find(c => c.display_name === upgradeOptions?.cycle.display_name);
      const newCycle = similarCycle || product.cycles[0];
      setSelectedCycle(newCycle.id);
      await getUpgradeOptions(newCycle.id, productId);
    } else if (productId === upgradeOptions?.product.id) {
      // Back to current product
      await getUpgradeOptions(undefined, undefined);
      setSelectedCycle(upgradeOptions.cycle.id);
    }
    checkIfOptionsChanged();
  };

  const displayCycle = (cycle: any, productChanged = false) => {
    if (!upgradeOptions) return true;
    
    if (productChanged) {
      if (!upgradeOptions.enduser_shorten_new_product_service_cycle) {
        return cycle.period_in_days >= upgradeOptions.cycle.period_in_days;
      }
    } else {
      if (!upgradeOptions.enduser_shorten_current_product_service_cycle) {
        return cycle.period_in_days >= upgradeOptions.cycle.period_in_days;
      }
    }
    return true;
  };

  useEffect(() => {
    getService();
    getUpgradeOptions();
  }, [serviceId]);

  useEffect(() => {
    checkIfOptionsChanged();
  }, [selectedProduct, selectedCycle, upgradeOptions, configOptionValues]);

  const renderConfigurableOption = (option: any) => {
    const value = configOptionValues[option.id] || '';

    // Render based on widget type
    if (option.widget === 'select' || option.widget === 'dropdown') {
      return (
        <FormRow key={option.id}>
          <FormColumn>
            <Select
              title={option.display || option.name}
              data={option.values?.map((v: any) => ({
                id: v.id || v.value,
                title: `${v.name || v.value} - ${v.price || 0} ${option.currency || ''}`,
              })) || []}
              selectedValue={value}
              onChange={(selected) => {
                setConfigOptionValues(prev => ({
                  ...prev,
                  [option.id]: selected.id.toString()
                }));
                checkIfOptionsChanged();
              }}
            />
          </FormColumn>
        </FormRow>
      );
    } else if (option.widget === 'yesno') {
      return (
        <FormRow key={option.id}>
          <FormColumn>
            <Select
              title={option.display || option.name}
              data={[
                { id: 'yes', title: 'Yes' },
                { id: 'no', title: 'No' },
              ]}
              selectedValue={value || 'no'}
              onChange={(selected) => {
                setConfigOptionValues(prev => ({
                  ...prev,
                  [option.id]: selected.id
                }));
                checkIfOptionsChanged();
              }}
            />
          </FormColumn>
        </FormRow>
      );
    } else if (option.widget === 'quantity' || option.widget === 'number') {
      return (
        <FormRow key={option.id}>
          <FormColumn>
            <Input
              title={option.display || option.name}
              values={value}
              handleChange={(newValue) => {
                setConfigOptionValues(prev => ({
                  ...prev,
                  [option.id]: newValue
                }));
                checkIfOptionsChanged();
              }}
              type="number"
            />
          </FormColumn>
        </FormRow>
      );
    } else {
      // Default text input
      return (
        <FormRow key={option.id}>
          <FormColumn>
            <Input
              title={option.display || option.name}
              values={value}
              handleChange={(newValue) => {
                setConfigOptionValues(prev => ({
                  ...prev,
                  [option.id]: newValue
                }));
                checkIfOptionsChanged();
              }}
            />
          </FormColumn>
        </FormRow>
      );
    }
  };

  if (pageLoading) {
    return (
      <Container>
        <TopProgress loading={pageLoading} />
        <Typography variant="h1">{t("LOADING")}...</Typography>
      </Container>
    );
  }

  if (!service || !upgradeOptions) return null;

  const selectedProductData = upgradeOptions.upgrades.find(p => p.id === selectedProduct);
  const upgradeInProgress = !!upgradeOptions.existing_upgrade_invoice;
  const canBeModified = upgradeOptions.cycle_upgrades.length > 0 || 
                       upgradeOptions.upgrades.length > 0 || 
                       upgradeOptions.configurable_options_upgrades.length > 0;

  if (!canBeModified) {
    return (
      <Container>
        <BackArrow fallbackPath={`/billing/services/${serviceId}`} />

        <Typography variant="h1">
          {t("UPGRADE_SERVICE")}: {service?.product?.name || t("SERVICE")}
        </Typography>

        <FormSection>
          <Typography variant="body">
            {t("SERVICE_NO_UPGRADES")}
          </Typography>
        </FormSection>

        <ButtonWrapper>
          <Button
            variant="secondary"
            size="display"
            type="button"
            onClick={() => navigate(`/billing/services/${serviceId}`)}
          >
            {t("BACK")}
          </Button>
        </ButtonWrapper>
      </Container>
    );
  }

  if (upgradeInProgress) {
    return (
      <Container>
        <BackArrow fallbackPath={`/billing/services/${serviceId}`} />

        <Typography variant="h1">
          {t("UPGRADE_SERVICE")}: {service?.product?.name || t("SERVICE")}
        </Typography>

        <FormSection>
          <Typography variant="h2">{t("SERVICE_CANNOT_BE_MODIFIED")}</Typography>
          <Typography variant="body">
            {t("EXISTING_UPGRADE_INVOICE")}: #{upgradeOptions.existing_upgrade_invoice}
          </Typography>
        </FormSection>

        <ButtonWrapper>
          <Button
            variant="secondary"
            size="display"
            type="button"
            onClick={() => navigate(`/billing/services/${serviceId}`)}
          >
            {t("BACK")}
          </Button>
        </ButtonWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <BackArrow fallbackPath={`/billing/services/${serviceId}`} />

      <Typography variant="h1">
        {t("UPGRADE_SERVICE")}: {service.product.name}
      </Typography>

      {!confirmChanges ? (
        <form>
          <FormSection>
            <Typography variant="h2">{t("UPGRADE_DOWNGRADE_CHANGE_CYCLE")}</Typography>

            {upgradeOptions.has_not_reached_service_cycles && (
              <Typography variant="body" style={{ color: '#d32f2f', marginBottom: '15px' }}>
                {t("FUTURE_CYCLES_CANCEL_WARNING")}
              </Typography>
            )}

            <FormRow>
              <FormColumn>
                <Select
                  title={t("SELECT_NEW_PRODUCT")}
                  data={[
                    {
                      id: upgradeOptions.product.id,
                      title: `${t("CURRENT_PRODUCT")}: ${upgradeOptions.product.name}`,
                    },
                    ...upgradeOptions.upgrades.map(product => ({
                      id: product.id,
                      title: product.name,
                    }))
                  ]}
                  selectedValue={selectedProduct}
                  onChange={(selected) => handleProductChange(selected.id)}
                />
              </FormColumn>
            </FormRow>

            <FormRow>
              <FormColumn>
                <Select
                  title={t("SELECT_NEW_CYCLE")}
                  data={selectedProductData ?
                    selectedProductData.cycles
                      .filter(cycle => displayCycle(cycle, true))
                      .map(cycle => ({
                        id: cycle.id,
                        title: `${cycle.display_name} ${cycle.fixed_price} ${cycle.currency}`,
                      })) :
                    [
                      {
                        id: upgradeOptions.cycle.id,
                        title: `${t("CURRENT_CYCLE")}: ${upgradeOptions.cycle.display_name}`,
                      },
                      ...upgradeOptions.cycle_upgrades
                        .filter(cycle => displayCycle(cycle, false))
                        .map(cycle => ({
                          id: cycle.id,
                          title: `${cycle.display_name} ${cycle.fixed_price} ${cycle.currency}`,
                        }))
                    ]
                  }
                  selectedValue={selectedCycle}
                  onChange={(selected) => handleCycleChange(selected.id)}
                />
              </FormColumn>
            </FormRow>

            {/* Configurable Options */}
            {upgradeOptions.configurable_options_upgrades && upgradeOptions.configurable_options_upgrades.length > 0 && (
              <>
                <Typography variant="h2" customStyles={{ marginTop: '20px', marginBottom: '15px' }}>
                  {t("CONFIGURABLE_OPTIONS")}
                </Typography>
                {upgradeOptions.configurable_options_upgrades.map(option => renderConfigurableOption(option))}
              </>
            )}
          </FormSection>

          <ButtonWrapper>
            <Button
              variant="secondary"
              size="display"
              type="button"
              onClick={() => navigate(`/billing/services/${serviceId}`)}
              disabled={loading}
            >
              {t("CANCEL")}
            </Button>
            <Button
              variant="primary"
              size="display"
              type="button"
              onClick={checkUpgrade}
              disabled={!optionsChanged || loading}
              loading={loading}
            >
              {t("NEXT")}
            </Button>
          </ButtonWrapper>
        </form>
      ) : (
        <div>
          <FormSection>
            <Typography variant="h2">{t("UPGRADE_SUMMARY")}</Typography>

            {upgradeResult && (
              <UpgradeSummary>
                <SummaryRow>
                  <SummaryLabel>{upgradeResult.display_name}</SummaryLabel>
                  <SummaryValue>
                    {upgradeResult.product_upgrade_price} {upgradeResult.currency}
                  </SummaryValue>
                </SummaryRow>
                
                {Object.entries(upgradeResult.taxes_applied).map(([taxName, taxAmount]) => (
                  <SummaryRow key={taxName}>
                    <SummaryLabel>{taxName}</SummaryLabel>
                    <SummaryValue>
                      {taxAmount} {upgradeResult.currency}
                    </SummaryValue>
                  </SummaryRow>
                ))}
                
                <TotalRow>
                  <SummaryLabel>{t("TOTAL_DUE_NOW")}</SummaryLabel>
                  <SummaryValue>
                    {upgradeResult.total_due} {upgradeResult.currency}
                  </SummaryValue>
                </TotalRow>
              </UpgradeSummary>
            )}

            <Typography variant="body">
              {t("PRICE_CALCULATED_INFO")}
            </Typography>

            {upgradeOptions.has_not_reached_service_cycles && (
              <Typography variant="body" style={{ color: '#d32f2f' }}>
                {t("FUTURE_CYCLES_CANCEL_WARNING")}
              </Typography>
            )}
          </FormSection>

          <ButtonWrapper>
            <Button
              variant="secondary"
              size="display"
              type="button"
              onClick={() => navigate(`/billing/services/${serviceId}`)}
              disabled={loading}
            >
              {t("CANCEL")}
            </Button>
            <Button
              variant="primary"
              size="display"
              type="button"
              onClick={placeOrder}
              loading={loading}
            >
              {t("PLACE_ORDER")}
            </Button>
          </ButtonWrapper>
        </div>
      )}
    </Container>
  );
};

export default ServiceUpgradeForm;




